import {makeAutoObservable} from 'mobx';
import StudioRepository from '../../../../../data/repositories/StudioRepository';
import {
  ApiError,
  GENERIC_ERROR,
  errorMessage,
  isInsufficientCredits,
} from '../../../errors';
import {studioRequest} from '../../../store/studioRequests';
import type {JobView} from '../../../types';
import {parseExcludedPages} from '../createOptions';
import {createBrief, formProblem} from './createBrief';
import type CreateStore from './CreateStore';

/** The job a submit created, for the page to open its storyboard. */
export interface CreatedJob {
  id: string;
  /** An older Studio API dropped per-topic mode and planned a single microreel. */
  perTopicIgnored: boolean;
}

/**
 * Creating the job from the create page's form, and why the form can't go ahead. While a job
 * starts the page is busy and its form is locked.
 */
class SubmissionStore {
  private repository = new StudioRepository();
  private creating = false;
  public busy = false;
  public error: string | null = null;
  /** Creating the job was refused for too few AI credits: the message links to adding them. */
  public needsCredits = false;

  constructor(private readonly page: CreateStore) {
    makeAutoObservable<SubmissionStore, 'repository' | 'creating' | 'page'>(
      this,
      {repository: false, creating: false, page: false},
      {autoBind: true},
    );
  }

  /** Shows why the form can't go ahead, or clears the message with null. */
  public report(problem: string | null): null {
    this.error = problem;
    this.needsCredits = false;
    return null;
  }

  /**
   * Checks the form and creates the job, first uploading a picked video to the library, which
   * Studio reads videos from. Resolves with the job, or null when the form shows why not.
   */
  public async submit(): Promise<CreatedJob | null> {
    const {page} = this;
    const isCurrent = page.sessionCheck();
    if (this.creating || !isCurrent || page.locked) return null;
    const problem = formProblem(page);
    if (problem) return this.report(problem);
    const {source, organizationId} = page;
    let excludedPages: number[];
    try {
      // A video has no pages to leave out.
      excludedPages =
        source.isDocument && !source.video
          ? parseExcludedPages(source.excluded)
          : [];
    } catch (error) {
      return this.report((error as Error).message);
    }
    const {file, video} = source;
    const libraryFile = source.isDocument ? source.libraryFile : null;
    const document = source.isDocument && !video ? file : null;
    const brief = createBrief(page, {
      category: page.categories.category,
      excludedPages,
      libraryMediaFileId: libraryFile?.id || null,
    });
    this.creating = true;
    this.busy = true;
    this.report(null);
    try {
      // Studio reads a video from the library, so a picked video goes there first.
      if (video && file) {
        if (!organizationId) throw new ApiError(GENERIC_ERROR, 0);
        const uploaded = await source.uploadVideo(organizationId, file);
        if (!uploaded) return null;
        brief.libraryMediaFileId = uploaded.id || null;
      }
      // Studio is only routed inside an organization; the page's, as it is now.
      const view = await studioRequest<JobView>(page.organizationId, org =>
        this.repository.createJob(org, brief, document),
      );
      if (!isCurrent()) return null;
      return {
        id: view.job.id,
        perTopicIgnored:
          brief.reelMode === 'per_topic' &&
          view.job.brief?.reelMode !== 'per_topic',
      };
    } catch (error) {
      if (isCurrent()) this.failed(error);
      return null;
    }
  }

  public failed(error: unknown) {
    this.creating = false;
    const enforceAiCredits = this.page.options.enforceAiCredits !== false;
    this.error = errorMessage(error, enforceAiCredits);
    this.needsCredits = enforceAiCredits && isInsufficientCredits(error);
    this.busy = false;
    this.page.source.setUploading(false);
  }

  public reset() {
    this.creating = false;
    this.busy = false;
    this.report(null);
  }
}

export default SubmissionStore;
